/**
 * Single source of truth for the numeric Richtwerte (roof area / module count / annual yield)
 * used across all 21 pilot pages (7 kWp sizes × DE/ES/PL, pSEO-Story Task 04) - imported by every
 * page file instead of each language variant hardcoding its own numbers, so the same size always
 * shows the exact same figures regardless of language (a DE/ES/PL mismatch here would be a real,
 * user-visible inconsistency, not just a copy/paste slip).
 *
 * None of this comes from real Roofora Offer data (see PvKostenRechnerService.kt doc: too few
 * data points platform-wide) - these are widely-used German solar-industry rules of thumb,
 * explicitly labeled as Richtwerte wherever rendered (CLAUDE.md-Pflicht):
 * - ~440 Wp pro Modul (marktübliches Modul Stand 2026).
 * - ~6 m² Dachfläche pro kWp installierter Leistung.
 * - ~1.000 kWh Jahresertrag pro kWp (bundesweiter Mittelwert, reale Werte schwanken mit
 *   Ausrichtung/Neigung/Region - siehe /pv-rechner für eine dachspezifische Berechnung).
 */
export interface KwpSizeFacts {
  kwp: number
  roofAreaM2: number
  moduleCount: number
  annualYieldKwh: number
}

const WATTS_PER_MODULE = 440
const ROOF_AREA_M2_PER_KWP = 6
const ANNUAL_YIELD_KWH_PER_KWP = 1000

/** The 7 pilot sizes chosen for this rollout (task: pSEO-Story Task 04) - a subset of the full,
 *  verbindlich indexed kWp step list from Task 01 (content/landingPages/types.ts
 *  INDEXED_KWP_STEPS), omitting only 6 kWp for this pilot wave. */
export const PILOT_KWP_VALUES = [5, 8, 10, 12, 15, 20, 25] as const

export const KWP_SIZE_FACTS: Record<(typeof PILOT_KWP_VALUES)[number], KwpSizeFacts> = Object.fromEntries(
  PILOT_KWP_VALUES.map((kwp) => [
    kwp,
    {
      kwp,
      roofAreaM2: kwp * ROOF_AREA_M2_PER_KWP,
      moduleCount: Math.round((kwp * 1000) / WATTS_PER_MODULE),
      annualYieldKwh: kwp * ANNUAL_YIELD_KWH_PER_KWP,
    },
  ]),
) as Record<(typeof PILOT_KWP_VALUES)[number], KwpSizeFacts>
